import React, { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import { toast } from "sonner"
import { AgentAvatar } from "../AgentAvatar"
import { useCallStore } from "../../../stores"
import { toggleMute } from "../../../lib/twilio"
import { hangupCurrentCall, toggleHoldForCurrentCall } from "../../../lib/call-center/call-controls"
import { CallControls } from "../controls/CallControls"
import { DTMFKeypad } from "../controls/DTMFKeypad"
import { TransferModal } from "../modals/TransferModal"
import { TransferStatusPanel } from "../panels/TransferStatusPanel"

// Format phone number for display
function formatPhone(phone: string): string {
  if (!phone) return ""
  const cleaned = phone.replace(/\D/g, "")
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
  }
  if (cleaned.length === 11 && cleaned[0] === "1") {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`
  }
  return phone
}

// Format duration as mm:ss
function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`
}

interface OnCallStateProps {
  collapsed?: boolean
}

export function OnCallState({ collapsed }: OnCallStateProps) {
  const {
    currentCall,
    transferCall,
    muted,
    onHold,
    showKeypad,
    setShowKeypad,
    showTransferModal,
    setShowTransferModal,
    callStartTime,
    setMuted,
    setOnHold,
  } = useCallStore()

  const [duration, setDuration] = useState(0)

  // Update call duration every second
  useEffect(() => {
    if (!callStartTime) return

    const updateDuration = () => {
      const elapsed = Math.floor((Date.now() - callStartTime) / 1000)
      setDuration(elapsed)
    }

    updateDuration()
    const interval = setInterval(updateDuration, 1000)
    return () => clearInterval(interval)
  }, [callStartTime])

  if (!currentCall) return null

  // Get initials from caller name
  const getCallerInitials = () => {
    if (!currentCall.name) return "?"
    const parts = currentCall.name.split(" ")
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
    }
    return currentCall.name.slice(0, 2).toUpperCase()
  }

  const handleToggleMute = () => {
    const newMuted = toggleMute()
    setMuted(newMuted)
  }

  const handleToggleHold = async () => {
    const result = await toggleHoldForCurrentCall(onHold, setOnHold)
    if (result === "missing_sid") {
      toast.error("Unable to hold — call identifier not yet available. Try again in a moment.")
    }
  }

  const handleEndCall = async () => {
    await hangupCurrentCall()
  }

  const handleTransfer = () => {
    setShowTransferModal(true)
  }

  // Wrap caller info in link if matter or contact exists
  const CallerInfo = () => {
    const content = (
      <>
        <p className="text-[14px] font-bold text-white">
          {currentCall.name || "Unknown Caller"}
        </p>
        <p className="text-[12px] font-medium text-white/80">
          {formatPhone(currentCall.number)}
        </p>
      </>
    )

    if (currentCall.matter) {
      return (
        <Link
          to={`/matters/${currentCall.matter}`}
          className="text-center hover:opacity-80 transition-opacity"
        >
          {content}
        </Link>
      )
    }
    if (currentCall.contact) {
      return (
        <Link
          to={`/contacts/${currentCall.contact}`}
          className="text-center hover:opacity-80 transition-opacity"
        >
          {content}
        </Link>
      )
    }
    return <div className="text-center">{content}</div>
  }

  if (collapsed) {
    return (
      <>
        <div className="py-3 bg-[#0d1f2d]">
          <div className="flex flex-col items-center gap-2">
            <AgentAvatar initials={getCallerInitials()} status="on_call" size="sm" />
            <span className="text-[10px] font-mono text-success call-timer">
              {formatDuration(duration)}
            </span>
          </div>
        </div>
        {transferCall && <TransferStatusPanel collapsed />}
      </>
    )
  }

  return (
    <>
      <div className="px-4 py-5 bg-[#0d1f2d]">
        {/* Keypad overlay */}
        {showKeypad && <DTMFKeypad onClose={() => setShowKeypad(false)} />}

        <div className="flex flex-col items-center">
          {/* Header with timer */}
          <div className="w-full flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-success opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-success"></span>
              </span>
              <span className="text-[10px] font-semibold uppercase tracking-wider text-white/80">
                Active Call
              </span>
            </div>
            <span className="text-[13px] font-mono font-bold text-success call-timer">
              {formatDuration(duration)}
            </span>
          </div>

          {/* Caller Avatar */}
          <AgentAvatar initials={getCallerInitials()} status="on_call" size="md" />

          {/* Caller Info */}
          <div className="mt-3">
            <CallerInfo />
          </div>

          {/* Call Controls */}
          <div className="mt-4 w-full">
            <CallControls
              muted={muted}
              onHold={onHold}
              onToggleMute={handleToggleMute}
              onToggleHold={handleToggleHold}
              onTransfer={handleTransfer}
              onEndCall={handleEndCall}
              onKeypad={() => setShowKeypad(true)}
            />
          </div>
        </div>
      </div>

      {/* Transfer Status Panel - shows when transfer is in progress */}
      {transferCall && <TransferStatusPanel />}

      {/* Transfer Modal */}
      <TransferModal
        open={showTransferModal}
        onOpenChange={setShowTransferModal}
      />
    </>
  )
}
